import { Roles } from '@dataquiver/stoneage-roles';
import { Form, Formik, FormikProps } from 'formik';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { bleActions } from 'src/redux/ble/actions';
import * as yup from 'yup';
import { ControllerMessages } from '../../intl/ControllerMessages';
import { GenericMessages } from '../../intl/GenericMessages';
import { useAuthUserInfo } from '../../redux/auth/selectors';
import { selectBleConnectedDevice, selectBleDidPromptCreateController } from '../../redux/ble/selectors';
import { useLimitedMode } from '../../utils/auth/useLimitedMode';
import { useSocket } from '../../utils/socket';
import { SocketContextSentinel } from '../../utils/socket/context/sentinelSocketContext';
import { AppModal } from '../reusable-components/app-modal/AppModal';
import { useToast } from '../reusable-components/useToast';
import { fetchUserInfo } from 'src/redux/auth/thunk/fetchUserInfo';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from 'src/redux/types';
import { AnyAction } from 'redux';
import { Input } from '../reusable-components/Input';

interface ControllerFormFields {
  physicalSerial: string;
  description: string;
}

interface _CreateControllerFormProps {
  orgId: string;
}

const validationSchemaFactory = (intl: IntlShape) =>
  yup.object({
    physicalSerial: yup
      .string()
      .required(intl.formatMessage(GenericMessages.fieldRequiredLabel))
      .matches(/^\d{1,4}$/, intl.formatMessage(ControllerMessages.physicalSerialValueValidation)),
    description: yup.string().optional(),
  });

const _CreateControllerFormModal: FC<_CreateControllerFormProps> = ({ orgId }) => {
  const intl = useIntl();
  const validationSchema = useMemo(() => validationSchemaFactory(intl), [intl]);
  const { formatMessage } = intl;
  const dispatch = useDispatch<ThunkDispatch<AppState, undefined, AnyAction>>();

  const [present] = useToast();
  const connectedDevice = useSelector(selectBleConnectedDevice);
  const didPrompt = useSelector(selectBleDidPromptCreateController);

  const { create, get } = useSocket(SocketContextSentinel, 'controllers', undefined, undefined);
  const [registeredController, setRegisteredController] = useState<unknown | undefined>(undefined);
  const [fetchComplete, setFetchComplete] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!connectedDevice?.serialNumber) {
        return undefined;
      }
      try {
        const item = await get(connectedDevice.serialNumber);
        if (item) {
          setRegisteredController(item);
        }
        setFetchComplete(true);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [connectedDevice?.serialNumber, dispatch, fetchComplete, get]);

  const onCreateController = useCallback(
    async (values: ControllerFormFields) => {
      try {
        if (!connectedDevice) throw new Error('No device connected!');
        await create({
          _id: connectedDevice.serialNumber,
          description: values.description,
          org: orgId,
          ssn: connectedDevice.serialNumber,
          physicalSerial: values.physicalSerial,
        });
        dispatch(bleActions.dismissCreateController());
        dispatch(fetchUserInfo()); // Upon creation, the new controller is added to the user object, necessitating a data refetch for the most updated list of controller associated with user.
      } catch (e) {
        console.error('Could not save controller:', e);
        present(
          {
            header: formatMessage(ControllerMessages.createControllerFailedHeader),
            message: formatMessage(ControllerMessages.createControllerFailedMessage),
          },
          'danger',
        );
      }
    },
    [connectedDevice, create, dispatch, formatMessage, orgId, present],
  );

  /**
   * Whether or not the modal is open. Is true when there is a connected BLE device,
   * no matching controller in Sentinel Link, and the user has not dismissed this modal for this controller.
   */
  const isModalOpen = !!connectedDevice && fetchComplete && !registeredController && !didPrompt;

  return (
    <Formik
      key={connectedDevice?.deviceId?.toString()} // Reinitialize form each time the connected device changes.
      initialValues={{
        physicalSerial: '',
        description: '',
      }}
      onSubmit={onCreateController}
      validationSchema={validationSchema}
      validateOnChange
      validateOnBlur
    >
      {(formikProps: FormikProps<{ physicalSerial: string; description: string }>) => {
        return (
          <Form>
            <AppModal
              isOpen={isModalOpen}
              onClose={() => {
                dispatch(bleActions.dismissCreateController());
              }}
              title={formatMessage(ControllerMessages.createControllerFormTitle)}
              primaryButton={{
                label: formatMessage(GenericMessages.saveButtonLabel),
                onClick: () => formikProps.submitForm(),
                disabled: !formikProps.isValid || !formikProps.dirty,
              }}
            >
              <Input
                label={formatMessage(ControllerMessages.physicalSerialLabel)}
                value={formikProps.values.physicalSerial}
                placeholder={formatMessage(ControllerMessages.physicalSerialPlaceholder)}
                onChange={formikProps.handleChange}
                onBlur={formikProps.handleBlur}
                name="physicalSerial"
                id="create-controller-physical-serial"
                required
                errorText={formikProps.touched.physicalSerial ? formikProps.errors.physicalSerial : undefined}
              />
              <Input
                label={formatMessage(ControllerMessages.descriptionLabel)}
                value={formikProps.values.description}
                placeholder={formatMessage(ControllerMessages.descriptionPlaceholder)}
                onChange={formikProps.handleChange}
                name="description"
                id="create-controller-description"
              />
            </AppModal>
          </Form>
        );
      }}
    </Formik>
  );
};

/** Wraps the CreateControllerFormModal component and does user role checks up front, to avoid unnecessary networks calls associated with mounting the component. */
export const CreateControllerFormModal: FC = () => {
  const user = useAuthUserInfo();
  const { isLimited } = useLimitedMode();
  if (isLimited || !user?.role || user.role < Roles.roles.STONEAGE_ADMIN) return null;
  // eslint-disable-next-line react/jsx-pascal-case
  return <_CreateControllerFormModal orgId={user.org} />;
};
